import Store from 'repatch';
import State from '../core/State';
import LiftReportWebApi from '../service/persistent/LiftReportWebApi';
import ApiErrorHandler from '../service/ApiErrorHandler';
import { disposeLiftUpReport } from '../thunk/liftUpReport';
import { disposeCoverageReport } from '../thunk/coverageReport';
import { disposeIndexReport } from '../thunk/indexReport';
import { FrequencyValue, ChartDimensionState } from '@kaizenplatform/kaizen-chartjs';
import { DateTime } from 'luxon';
import { setFromPeriod, setToPeriod } from '../thunk/liftReportChartSetting';
import {
  setLiftReports,
  finishFetchingLiftReports,
  disposeLiftReports,
  startFetchingLiftReports,
} from '../thunk/liftReports';
import { setSelectedGoalId, disposeSelectedGoalId } from '../thunk/selectedGoal';
import { setIndexReportDimensionKeyState } from '../thunk/indexReportDimensionKeyState';
import {
  setCoverageReportDimensionKeyState,
  disposeCoverageReportDimensionKeyState,
} from '../thunk/coverageReportDimensionKeyState';
import {
  setLiftUpReportDimensionKeyState,
  disposeLiftUpReportDimensionKeyState,
} from '../thunk/liftUpReportDimensionKeyState';
import { setIndexReportFrequency } from '../thunk/indexReportFrequency';
import { setCoverageReportFrequency } from '../thunk/coverageReportFrequency';
import { setLiftUpReportFrequency } from '../thunk/liftUpReportFrequency';
import { createPath, History } from 'history';
import mergeUrlQuery from '../utility/mergeUrlQuery';
import UpdateLiftReport from '../entity/UpdateLiftReport';
import { SetStateAction } from 'react';
import MessageDispatcher from '../service/MessageDispatcher';
import LiftReport from '../entity/LiftReport';
import CreateLiftReportInput from '../entity/CreateLiftReportInput';
import RemoveLiftReportInput from '../entity/RemoveLiftReportInput';

class LiftReportUsecase {
  private store: Store<State>;
  private history: History;
  private liftReportWebApi: LiftReportWebApi;
  private apiErrorHandler: ApiErrorHandler;
  private messageDispatcher: MessageDispatcher;

  constructor({
    store,
    history,
    liftReportWebApi,
    apiErrorHandler,
    messageDispatcher,
  }: {
    store: Store<State>;
    history: History;
    liftReportWebApi: LiftReportWebApi;
    apiErrorHandler: ApiErrorHandler;
    messageDispatcher: MessageDispatcher;
  }) {
    this.store = store;
    this.history = history;
    this.liftReportWebApi = liftReportWebApi;
    this.apiErrorHandler = apiErrorHandler;
    this.messageDispatcher = messageDispatcher;
  }

  public prepareLiftReportCharts = (): void => {
    this.getLiftReports();
  };

  private getLiftReports = async (): Promise<void> => {
    this.dispose();
    this.store.dispatch(disposeLiftReports());
    this.store.dispatch(startFetchingLiftReports());
    const state = this.store.getState();
    const query = new URLSearchParams(state.location.search);

    const organizationId = state.lastAccessedOrganization.item!.id;

    try {
      const goals = await this.liftReportWebApi.getLiftReports(organizationId);

      if (goals.length == 0) {
        this.store.dispatch(finishFetchingLiftReports());
        return;
      }

      const goalId = query.get('goal_id') ? query.get('goal_id') : goals[0].goal.id;
      const isNoItem = !goals.find((goal) => goal.goal.id === goalId);

      const from =
        query.get('from') && DateTime.fromFormat(query.get('from')!, 'yyyyMMdd', { zone: 'utc' }).toISO()
          ? DateTime.fromFormat(query.get('from')!, 'yyyyMMdd', { zone: 'utc' }).toISO()
          : goals[0].activityPeriodFrom
          ? goals[0].activityPeriodFrom
          : DateTime.local().startOf('month').toISO();
      const to =
        query.get('to') && DateTime.fromFormat(query.get('to')!, 'yyyyMMdd', { zone: 'utc' }).toISO()
          ? DateTime.fromFormat(query.get('to')!, 'yyyyMMdd', { zone: 'utc' }).toISO()
          : goals[0].activityPeriodTo
          ? goals[0].activityPeriodTo
          : DateTime.local().endOf('month').toISO();

      this.store.dispatch(setFromPeriod(DateTime.fromISO(from)));
      this.store.dispatch(setToPeriod(DateTime.fromISO(to)));

      this.store.dispatch(setLiftReports(goals));

      if (isNoItem) {
        this.store.dispatch(setSelectedGoalId(goals[0].goal.id));
      } else {
        this.store.dispatch(setSelectedGoalId(goalId!));
      }
    } catch (error) {
      this.apiErrorHandler.handle(error);
    } finally {
      this.setGoalIdToURL();
      this.setFromToURL();
      this.setToToURL();

      this.store.dispatch(finishFetchingLiftReports());
    }
  };

  private getSelectedLiftReportPeriod = () => {
    const state = this.store.getState();

    if (!state.liftReports.data) {
      return;
    }

    const selectedLiftReport = state.liftReports.data.find((liftReport) => {
      return liftReport.goal.id === state.selectedGoalId.data;
    });

    if (!selectedLiftReport) {
      return;
    }
    const from = selectedLiftReport.activityPeriodFrom
      ? selectedLiftReport.activityPeriodFrom
      : DateTime.local().startOf('month').toISO();
    const to = selectedLiftReport.activityPeriodTo
      ? selectedLiftReport.activityPeriodTo
      : DateTime.local().endOf('month').toISO();

    this.store.dispatch(setFromPeriod(DateTime.fromISO(from)));
    this.store.dispatch(setToPeriod(DateTime.fromISO(to)));

    this.setFromToURL();
    this.setToToURL();
  };

  public setFromPeriod = (date: DateTime | null): void => {
    if (date) {
      this.store.dispatch(setFromPeriod(date));
      this.setFromToURL();
    }
  };

  public setToPeriod = (date: DateTime | null): void => {
    if (date) {
      this.store.dispatch(setToPeriod(date));
      this.setToToURL();
    }
  };

  public setSelectedGoal = (selectedGoalId: string | null): void => {
    if (selectedGoalId) {
      this.store.dispatch(setSelectedGoalId(selectedGoalId));
      this.getSelectedLiftReportPeriod();
      this.setGoalIdToURL();
    }
  };

  public setIndexReportDimensionKeyState = (indexReportDimensionKeyState: ChartDimensionState[] | null) => {
    if (indexReportDimensionKeyState) {
      this.store.dispatch(setIndexReportDimensionKeyState(indexReportDimensionKeyState));
    }
  };

  public setCoverageReportDimensionKeyState = (coverageReportDimensionKeyState: ChartDimensionState[] | null) => {
    if (coverageReportDimensionKeyState) {
      this.store.dispatch(setCoverageReportDimensionKeyState(coverageReportDimensionKeyState));
    }
  };

  public setLiftUpReportDimensionKeyState = (liftUpReportDimensionKeyState: ChartDimensionState[] | null) => {
    if (liftUpReportDimensionKeyState) {
      this.store.dispatch(setLiftUpReportDimensionKeyState(liftUpReportDimensionKeyState));
    }
  };

  public setIndexReportFrequency = (frequency: FrequencyValue | null) => {
    if (frequency) {
      this.store.dispatch(setIndexReportFrequency(frequency));
    }
  };

  public setCoverageReportFrequency = (frequency: FrequencyValue | null) => {
    if (frequency) {
      this.store.dispatch(setCoverageReportFrequency(frequency));
    }
  };

  public setLiftUpReportFrequency = (frequency: FrequencyValue | null) => {
    if (frequency) {
      this.store.dispatch(setLiftUpReportFrequency(frequency));
    }
  };

  public dispose = (): void => {
    this.store.dispatch(disposeLiftUpReport());
    this.store.dispatch(disposeCoverageReport());
    this.store.dispatch(disposeIndexReport());
    this.store.dispatch(disposeLiftUpReportDimensionKeyState());
    this.store.dispatch(disposeCoverageReportDimensionKeyState());
    this.store.dispatch(disposeIndexReport());
    this.store.dispatch(disposeSelectedGoalId());
  };

  public setGoalIdToURL = async (): Promise<void> => {
    const state = this.store.getState();
    const selectedGoalId = state.selectedGoalId.data;

    this.history.push(createPath(mergeUrlQuery(this.history.location, { goal_id: selectedGoalId })));
  };

  public setFromToURL = async (): Promise<void> => {
    const state = this.store.getState();
    const from = state.liftReportChartSetting.period.from;

    this.history.push(createPath(mergeUrlQuery(this.history.location, { from: from.toFormat('yyyyMMdd') })));
  };

  public setToToURL = async (): Promise<void> => {
    const state = this.store.getState();
    const to = state.liftReportChartSetting.period.to;

    this.history.push(createPath(mergeUrlQuery(this.history.location, { to: to.toFormat('yyyyMMdd') })));
  };

  public updateLiftReportPeriod = async (
    setDisabled: (value: SetStateAction<boolean>) => void,
    setLoadingUpdate: (value: SetStateAction<boolean>) => void,
  ): Promise<void> => {
    const state = this.store.getState();

    const selectedLiftReport = state.liftReports.data?.find((liftReport) => {
      return liftReport.goal.id === state.selectedGoalId.data;
    });

    if (!selectedLiftReport) {
      return;
    }

    const from = state.liftReportChartSetting.period.from;
    const to = state.liftReportChartSetting.period.to;

    const updateLiftReport = new UpdateLiftReport({
      id: selectedLiftReport.id,
      activityPeriodFrom: from.toISO(),
      activityPeriodTo: to.toISO(),
    });

    try {
      const liftReport = await this.liftReportWebApi.updateLiftreportPeriod(updateLiftReport);
      const newLiftReports = state.liftReports.data?.map((value) => {
        if (value.id == liftReport.id) {
          return new LiftReport({
            id: value.id,
            organization: value.organization,
            goal: value.goal,
            charts: value.charts,
            enabled: value.enabled,
            mainGoal: value.mainGoal,
            activityPeriodFrom: liftReport.activityPeriodFrom,
            activityPeriodTo: liftReport.activityPeriodTo,
          });
        } else {
          return value;
        }
      });

      if (newLiftReports) {
        this.store.dispatch(setLiftReports(newLiftReports));
      }
      setDisabled(true);
      this.messageDispatcher.notifyUnimportant('期間の更新が完了しました');
    } catch (error) {
      this.apiErrorHandler.handleMutationError(error, '期間の更新に失敗しました');
    } finally {
      setLoadingUpdate(false);
    }
  };

  public createLiftReport = async (goalId: string, mainGoal: boolean): Promise<void> => {
    const state = this.store.getState();

    const createLiftReportInput = new CreateLiftReportInput({ goalId: goalId, mainGoal: mainGoal });

    try {
      const liftReport = await this.liftReportWebApi.createLiftreportPeriod(createLiftReportInput);
      const newLiftReports = state.liftReports.data?.map((value) => {
        if (value.id == liftReport.id) {
          return new LiftReport({
            id: value.id,
            organization: value.organization,
            goal: value.goal,
            charts: value.charts,
            enabled: value.enabled,
            mainGoal: value.mainGoal,
            activityPeriodFrom: liftReport.activityPeriodFrom,
            activityPeriodTo: liftReport.activityPeriodTo,
          });
        } else {
          return value;
        }
      });

      if (newLiftReports) {
        this.store.dispatch(setLiftReports(newLiftReports));
      }

      this.messageDispatcher.notifyUnimportant('レポートの作成が完了しました');
    } catch (error) {
      this.apiErrorHandler.handleMutationError(error, 'レポートの作成に失敗しました');
    }
  };

  public removeLiftReport = async (goalId: string): Promise<void> => {
    const removeLiftReportInput = new RemoveLiftReportInput({ goalId: goalId });

    try {
      await this.liftReportWebApi.removeLiftreportPeriod(removeLiftReportInput);

      this.messageDispatcher.notifyUnimportant('レポートの削除が完了しました');
    } catch (error) {
      this.apiErrorHandler.handleMutationError(error, 'レポートの削除に失敗しました');
    }
  };
}

export default LiftReportUsecase;
